import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { ReportViewStore } from "../../store";
import { ReportView } from "../view/ReportView";
import { ReportUpdateStore } from "../../store";
import { ReportUpdate } from "../update/ReportUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type ReportModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const ReportModalContent: React.FC<ReportModalContentProps> = observer(
  ({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new ReportUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new ReportViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <ReportView reportViewStore={storeInfo.store as ReportViewStore} />
        )}
        {storeInfo.type === "update" && (
          <ReportUpdate
            reportUpdateStore={storeInfo.store as ReportUpdateStore}
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  }
);
